import React, { useState } from "react";
import api from "@/lib/api";

/**
 * Admin-only browser diagnostic for transactional email.
 *
 * Hits the protected /api/admin/health/email and /api/admin/health/email/send-test
 * endpoints. Requires the operator to paste the production INTERNAL_API_KEY in
 * a one-time field (stored in sessionStorage so it survives reloads without
 * being persisted to disk).
 *
 * Route is intentionally unlinked from the public nav — open it manually at
 * /admin/email-health when something looks broken.
 */
export default function EmailHealth() {
  const [key, setKey] = useState(() => sessionStorage.getItem("tf_admin_key") || "");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const saveKey = (v) => { setKey(v); sessionStorage.setItem("tf_admin_key", v); };

  const checkConfig = async () => {
    setStatus(null); setLoading(true);
    try {
      const { data } = await api.get("/admin/health/email", { headers: { "X-Internal-Key": key } });
      setStatus({ ok: true, data });
    } catch (e) {
      setStatus({ ok: false, code: e.response?.status, detail: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const sendTest = async () => {
    if (!to) return;
    setSendResult(null); setLoading(true);
    try {
      const { data } = await api.post("/admin/health/email/send-test", { to }, { headers: { "X-Internal-Key": key } });
      setSendResult({ ok: true, data });
    } catch (e) {
      setSendResult({ ok: false, code: e.response?.status, detail: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="label-overline text-[#FFCC00]">Admin diagnostic</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">EMAIL HEALTH</h1>
        <p className="text-sm text-zinc-400 mt-2">
          Unlinked diagnostic page. Use this to confirm transactional email is wired
          correctly when password-reset emails stop arriving.
        </p>

        <div className="mt-8 space-y-3">
          <label className="label-overline block">Internal API key</label>
          <input
            data-testid="admin-internal-key"
            type="password"
            value={key}
            onChange={(e) => saveKey(e.target.value)}
            placeholder="Paste the INTERNAL_API_KEY from production env"
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FFCC00] px-3 py-3 text-sm font-mono-tag outline-none"
          />
          <p className="text-[11px] text-zinc-500">
            Stored in sessionStorage only — never persisted to disk. Disappears
            when you close the tab.
          </p>
        </div>

        <div className="mt-8 border border-[#262626] bg-[#141414] p-5">
          <div className="font-display text-xl tracking-tight">1. Check config</div>
          <p className="text-xs text-zinc-400 mt-1">
            Confirms which Resend API key, FROM address and FRONTEND_URL the
            production backend picked up.
          </p>
          <button
            type="button"
            onClick={checkConfig}
            disabled={loading || !key}
            data-testid="email-health-check"
            className="kinetic-btn mt-4 bg-[#FFCC00] hover:bg-[#e8b800] disabled:opacity-50 text-black px-4 py-2.5 font-bold uppercase tracking-widest text-xs"
          >
            {loading ? "Checking…" : "Check email config"}
          </button>
          {status && (
            <pre
              data-testid="email-health-status"
              className={`mt-4 p-3 text-xs font-mono-tag whitespace-pre-wrap break-all border ${
                status.ok ? "border-[#34C759]/40 bg-[#34C759]/5 text-[#34C759]" : "border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF3B30]"
              }`}
            >{JSON.stringify(status, null, 2)}</pre>
          )}
        </div>

        <div className="mt-6 border border-[#262626] bg-[#141414] p-5">
          <div className="font-display text-xl tracking-tight">2. Send a real test email</div>
          <p className="text-xs text-zinc-400 mt-1">
            Sends one transactional email through Resend and surfaces the exact
            response — quota / domain / API-key error or the message ID on success.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <input
              data-testid="email-health-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 min-w-[240px] bg-[#0A0A0A] border border-[#262626] focus:border-[#FFCC00] px-3 py-2.5 text-sm outline-none"
            />
            <button
              type="button"
              onClick={sendTest}
              disabled={loading || !key || !to}
              data-testid="email-health-send"
              className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-50 text-white px-4 py-2.5 font-bold uppercase tracking-widest text-xs"
            >
              {loading ? "Sending…" : "Send test"}
            </button>
          </div>
          {sendResult && (
            <pre
              data-testid="email-health-result"
              className={`mt-4 p-3 text-xs font-mono-tag whitespace-pre-wrap break-all border ${
                sendResult.ok && sendResult.data?.ok
                  ? "border-[#34C759]/40 bg-[#34C759]/5 text-[#34C759]"
                  : "border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF3B30]"
              }`}
            >{JSON.stringify(sendResult, null, 2)}</pre>
          )}
        </div>

        <div className="mt-6 text-xs text-zinc-500 leading-relaxed border-l-2 border-[#262626] pl-4">
          <div className="text-zinc-300 font-semibold uppercase tracking-widest text-[11px]">Reading the result</div>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            <li><code>ok: true</code> + message id → email sent. Check spam, then Resend → Emails tab for delivery status.</li>
            <li><code>error: "resend_not_configured"</code> → <code>RESEND_API_KEY</code> missing in production env.</li>
            <li><code>"Domain … is not verified"</code> → re-verify the FROM domain in Resend dashboard.</li>
            <li><code>"API key … is invalid"</code> → regenerate the key in Resend, paste into prod env.</li>
            <li><code>rate limit</code> / <code>quota</code> → upgrade Resend plan (yours is fine: 8/3000).</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
